/**
 * Atlas ACP-017 — unified desktop mega-menu interaction.
 * Extends Athens without changing Shopify menu data or the inherited mobile drawer.
 */
(() => {
  "use strict";

  const DESKTOP_MEDIA_QUERY = "(min-width: 990px)";
  const HOVER_MEDIA_QUERY = "(hover: hover) and (pointer: fine)";
  const OPEN_DELAY = 100;
  const CLOSE_DELAY = 160;

  const initNavigation = (navigation) => {
    if (navigation.dataset.atlasNavigationReady === "true") {
      return;
    }

    const menus = [...navigation.querySelectorAll(".atlas-navigation__menu")];
    const items = [...navigation.querySelectorAll("[data-atlas-menu-item]")];
    const triggers = items.map((item) => item.querySelector("[data-atlas-menu-trigger]"));
    const backdrop = navigation.querySelector("[data-atlas-navigation-backdrop]");
    const desktopMedia = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const hoverMedia = window.matchMedia(HOVER_MEDIA_QUERY);

    let openItem = null;
    let openTimer = null;
    let closeTimer = null;

    const clearTimers = () => {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
    };

    const updateTriggerState = (trigger, expanded) => {
      const state = trigger?.querySelector("[data-atlas-menu-state]");

      trigger?.setAttribute("aria-expanded", expanded ? "true" : "false");

      if (state) {
        state.textContent = expanded ? trigger.dataset.collapseText : trigger.dataset.expandText;
      }
    };

    const closeItem = (item, restoreFocus = false) => {
      if (!item) {
        return;
      }

      const trigger = item.querySelector("[data-atlas-menu-trigger]");
      const panel = item.querySelector("[data-atlas-menu-panel]");

      item.classList.remove("is-open");
      delete item.dataset.atlasOpenSource;
      updateTriggerState(trigger, false);
      panel?.setAttribute("aria-hidden", "true");
      panel?.setAttribute("inert", "");

      if (restoreFocus) {
        trigger?.focus();
      }

      if (openItem === item) {
        openItem = null;
      }

      if (!openItem) {
        navigation.classList.remove("is-menu-open");
      }
    };

    const closeAll = (restoreFocus = false) => {
      clearTimers();
      const itemToRestore = restoreFocus ? openItem : null;

      items.forEach((item) => closeItem(item));

      if (itemToRestore) {
        itemToRestore.querySelector("[data-atlas-menu-trigger]")?.focus();
      }
    };

    const openMenu = (item, focusPanel = false, focusLast = false, source = "control") => {
      if (!desktopMedia.matches || !item) {
        return;
      }

      clearTimers();

      items.forEach((candidate) => {
        if (candidate !== item) {
          closeItem(candidate);
        }
      });

      const trigger = item.querySelector("[data-atlas-menu-trigger]");
      const panel = item.querySelector("[data-atlas-menu-panel]");

      item.classList.add("is-open");
      item.dataset.atlasOpenSource = source;
      updateTriggerState(trigger, true);
      panel?.setAttribute("aria-hidden", "false");
      panel?.removeAttribute("inert");
      navigation.classList.add("is-menu-open");
      openItem = item;

      if (focusPanel && panel) {
        const focusable = [
          ...panel.querySelectorAll(
            'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        ];
        const target = focusLast ? focusable.at(-1) : focusable[0];
        target?.focus();
      }
    };

    const scheduleOpen = (item) => {
      clearTimers();
      openTimer = window.setTimeout(() => openMenu(item, false, false, "hover"), OPEN_DELAY);
    };

    const scheduleClose = (item) => {
      clearTimers();
      closeTimer = window.setTimeout(() => {
        if (!item.matches(":hover") && !item.contains(document.activeElement)) {
          closeItem(item);
        }
      }, CLOSE_DELAY);
    };

    const focusAdjacentTrigger = (currentTrigger, offset) => {
      const currentIndex = triggers.indexOf(currentTrigger);

      if (currentIndex < 0) {
        return;
      }

      closeAll();
      const nextIndex = (currentIndex + offset + triggers.length) % triggers.length;
      triggers[nextIndex]?.focus();
    };

    menus.forEach((menu) => menu.classList.add("is-enhanced"));
    items.forEach((item) => {
      const trigger = item.querySelector("[data-atlas-menu-trigger]");
      const panel = item.querySelector("[data-atlas-menu-panel]");

      panel?.setAttribute("aria-hidden", "true");
      panel?.setAttribute("inert", "");

      trigger?.addEventListener("click", () => {
        if (item.classList.contains("is-open") && item.dataset.atlasOpenSource === "control") {
          closeItem(item);
        } else {
          openMenu(item, false, false, "control");
        }
      });

      trigger?.addEventListener("keydown", (event) => {
        switch (event.key) {
          case "ArrowDown":
            event.preventDefault();
            openMenu(item, true);
            break;
          case "ArrowUp":
            event.preventDefault();
            openMenu(item, true, true);
            break;
          case "ArrowRight":
            event.preventDefault();
            focusAdjacentTrigger(trigger, 1);
            break;
          case "ArrowLeft":
            event.preventDefault();
            focusAdjacentTrigger(trigger, -1);
            break;
          case "Home":
            event.preventDefault();
            closeAll();
            triggers[0]?.focus();
            break;
          case "End":
            event.preventDefault();
            closeAll();
            triggers.at(-1)?.focus();
            break;
          case "Escape":
            event.preventDefault();
            closeItem(item, true);
            break;
        }
      });

      panel?.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          closeItem(item, true);
        }
      });

      item.addEventListener("mouseenter", () => {
        if (hoverMedia.matches) {
          scheduleOpen(item);
        }
      });

      item.addEventListener("mouseleave", () => {
        if (hoverMedia.matches) {
          scheduleClose(item);
        }
      });

      item.addEventListener("focusout", (event) => {
        if (!item.contains(event.relatedTarget)) {
          scheduleClose(item);
        }
      });
    });

    backdrop?.addEventListener("click", () => closeAll());

    document.addEventListener("click", (event) => {
      if (openItem && !navigation.contains(event.target)) {
        closeAll();
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && openItem) {
        event.preventDefault();
        closeAll(true);
      }
    });

    const handleViewportChange = (event) => {
      if (!event.matches) {
        closeAll();
      }
    };

    if (desktopMedia.addEventListener) {
      desktopMedia.addEventListener("change", handleViewportChange);
    } else {
      desktopMedia.addListener(handleViewportChange);
    }

    navigation.dataset.atlasNavigationReady = "true";
  };

  const initAllNavigations = (root = document) => {
    root.querySelectorAll("[data-atlas-navigation]").forEach(initNavigation);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAllNavigations());
  } else {
    initAllNavigations();
  }

  document.addEventListener("shopify:section:load", (event) => initAllNavigations(event.target));
})();
